import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { ListSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { fullDate } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const KIND_LABELS = {
    welcome: "Bienvenida",
    daily: "Resumen diario",
    reset: "Restablecer contraseña",
    team_join: "Nuevo miembro",
};

const STATUS_LABELS = {
    logged: "Registrado",
    sent: "Enviado",
    queued: "En cola",
    failed: "Fallido",
};

export default function Correos() {
    const [items, setItems] = useState(null);
    const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 });
    const [page, setPage] = useState(1);

    const load = useCallback(async () => {
        setItems(null);
        try {
            const { data } = await api.get("/emails", { params: { page, limit: 15 } });
            setItems(data.items);
            setMeta({ page: data.page, pages: data.pages, total: data.total });
        } catch (e) {
            toast.error(errMsg(e));
            setItems([]);
        }
    }, [page]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-8" data-testid="correos-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Correos</h1>
                <p className="mt-1 text-nm-muted">Todo lo que Network Mastery ha preparado para ti, con su fecha y estado.</p>
            </div>

            <div className="rounded-2xl border border-nm-line bg-nm-accent-soft/50 p-4 text-sm text-nm-ink">
                Aún no hay un proveedor de correo conectado, así que los mensajes se{" "}
                <span className="font-semibold">registran aquí</span> en lugar de enviarse. Cuando se
                configuren las claves, empezarán a salir de verdad.
            </div>

            {items === null ? (
                <ListSkeleton rows={4} />
            ) : items.length === 0 ? (
                <EmptyState testId="correos-empty" title="Todavía no hay correos"
                    description="En cuanto haya movimiento (una bienvenida, tu resumen diario...), lo verás aquí." />
            ) : (
                <>
                    <div className="divide-y divide-nm-line overflow-hidden rounded-2xl border border-nm-line bg-nm-surface">
                        {items.map((e) => (
                            <div key={e.id} className="flex items-center gap-4 p-4" data-testid={`email-item-${e.id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="rounded-full border border-nm-line px-2 py-0.5 text-xs font-medium text-nm-muted">
                                            {KIND_LABELS[e.kind] || e.kind}
                                        </span>
                                        <p className="truncate font-semibold text-nm-ink">{e.subject}</p>
                                    </div>
                                    <p className="mt-1 truncate text-sm text-nm-muted">Para {e.to} · {fullDate(e.created_at)}</p>
                                </div>
                                <span className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium",
                                    e.status === "failed" ? "bg-destructive/10 text-destructive" : "bg-nm-accent-soft text-nm-accent")}>
                                    {STATUS_LABELS[e.status] || e.status}
                                </span>
                            </div>
                        ))}
                    </div>

                    {meta.pages > 1 && (
                        <div className="flex items-center justify-between">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                            <span className="text-sm text-nm-muted tabular-nums">Página {meta.page} de {meta.pages}</span>
                            <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
